"use server";

import { revalidatePath } from "next/cache";
import { AuthError } from "next-auth";
import { signIn, signOut } from "@/auth";
import { requireApiActor, type Actor } from "@/lib/auth/dal";
import { createTask, updateTaskStatus } from "@/lib/services/task-service";
import { createSite } from "@/lib/services/site-service";
import { createChangeOrder } from "@/lib/services/change-order-service";
import { createDailyReport } from "@/lib/services/daily-report-service";
import { updateFinanceSettings } from "@/lib/services/settings-service";
import { sendChatMessage } from "@/lib/services/chat-service";
import { decideApprovalRequest, prepareProposal } from "@/lib/services/approval-service";
import {
  authorizeApprovalDecisionAction,
  authorizeChangeOrderCreationAction,
  authorizeDailyReportCreationAction,
  authorizeChatThreadAction,
  authorizeTaskCreationAction,
  authorizeTaskStatusAction,
} from "@/lib/services/action-authorization";
import { recordMutationFailure } from "@/lib/services/audit";

import type { ActionDraft, AssistantStatus } from "@/lib/assistant-core/contracts";
import { validateConfirmation } from "@/lib/assistant-core/intents";

const text = (data: FormData, key: string) => String(data.get(key) ?? "");
const nullable = (data: FormData, key: string) => text(data, key).trim() || null;
const version = (data: FormData) => {
  const raw = text(data, "version");
  if (!/^(0|[1-9]\d*)$/.test(raw)) throw new Error("INVALID_VERSION");
  const value = Number(raw);
  if (!Number.isSafeInteger(value)) throw new Error("INVALID_VERSION");
  return value;
};
export type FormActionState = { ok: boolean; message: string; answer?: string; threadId?: string; assistantStatus?: AssistantStatus; actionDraft?: ActionDraft };

function errorCode(error: unknown): string {
  if (error && typeof error === "object" && "code" in error && typeof error.code === "string") return error.code;
  return error instanceof Error ? error.message : "UNEXPECTED_ERROR";
}

async function auditFailure(actor: Actor | null, module: string, action: string, error: unknown) {
  if (actor) await recordMutationFailure(actor, module, action, error);
}

function parseDateTimeLocal(value: string | null): string | null {
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3}))?)?$/.exec(value);
  if (!match) throw new Error("INVALID_DATE");
  const [, yearRaw, monthRaw, dayRaw, hourRaw, minuteRaw, secondRaw = "0", millisecondRaw = "0"] = match;
  const [year, month, day, hour, minute, second] = [yearRaw, monthRaw, dayRaw, hourRaw, minuteRaw, secondRaw].map(Number);
  const millisecond = Number(millisecondRaw.padEnd(3, "0"));
  const leapYear = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  const daysInMonth = [31, leapYear ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  if (year < 1 || month < 1 || month > 12 || day < 1 || day > daysInMonth[month - 1] || hour > 23 || minute > 59 || second > 59) {
    throw new Error("INVALID_DATE");
  }
  const wallTime = Date.UTC(year, month - 1, day, hour, minute, second, millisecond);
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Berlin",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  });
  const partsAt = (instant: number) => Object.fromEntries(
    formatter.formatToParts(new Date(instant)).filter(part => part.type !== "literal").map(part => [part.type, Number(part.value)]),
  ) as Record<string, number>;
  const offsetAt = (instant: number) => {
    const parts = partsAt(instant);
    return Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second) - Math.trunc(instant / 1000) * 1000;
  };
  let instant = wallTime;
  for (let iteration = 0; iteration < 4; iteration += 1) {
    const next = wallTime - offsetAt(instant);
    if (next === instant) break;
    instant = next;
  }
  const resolved = partsAt(instant);
  if (resolved.year !== year || resolved.month !== month || resolved.day !== day || resolved.hour !== hour || resolved.minute !== minute || resolved.second !== second) {
    throw new Error("INVALID_DATE");
  }
  return new Date(instant).toISOString();
}

export async function loginAction(_previousState: FormActionState, formData: FormData): Promise<FormActionState> {
  try {
    await signIn("credentials", { email: text(formData, "email"), password: text(formData, "password"), redirectTo: "/command-center" });
    return { ok: true, message: "Anmeldung erfolgreich." };
  } catch (error) {
    if (error instanceof AuthError) {
      return { ok: false, message: error.type === "CredentialsSignin" ? "E-Mail oder Passwort ist ungültig." : "Die Anmeldung ist derzeit nicht möglich." };
    }
    throw error;
  }
}

export async function logoutAction() {
  await signOut({ redirectTo: "/login" });
}

export async function createTaskAction(_previousState: FormActionState, formData: FormData): Promise<FormActionState> {
  let actor: Actor | null = null;
  try {
    actor = await requireApiActor();
    const baustelleId = nullable(formData, "baustelleId");
    const assigneeId = nullable(formData, "assigneeId");
    await authorizeTaskCreationAction(actor, baustelleId, assigneeId);
    const dueAt = parseDateTimeLocal(nullable(formData, "dueAt"));
    await createTask(actor, {
      title: text(formData, "title"),
      description: text(formData, "description"),
      assigneeId,
      baustelleId,
      dueAt,
      priority: text(formData, "priority"),
    });
    revalidatePath("/aufgaben");
    revalidatePath("/command-center");
    return { ok: true, message: "Aufgabe wurde angelegt." };
  } catch (error) {
    await auditFailure(actor, "TASK", "CREATE", error);
    return { ok: false, message: errorCode(error) === "INVALID_DATE" ? "Das Fälligkeitsdatum ist ungültig." : "Aufgabe konnte nicht angelegt werden. Eingaben und Berechtigungen prüfen." };
  }
}

export async function updateTaskStatusAction(_previousState: FormActionState, formData: FormData): Promise<FormActionState> {
  let actor: Actor | null = null;
  try {
    actor = await requireApiActor();
    const taskId = text(formData, "taskId");
    await authorizeTaskStatusAction(actor, taskId);
    await updateTaskStatus(actor, { taskId, status: text(formData, "status"), version: version(formData) });
    revalidatePath("/aufgaben");
    revalidatePath("/command-center");
    return { ok: true, message: "Aufgabenstatus wurde aktualisiert." };
  } catch (error) {
    await auditFailure(actor, "TASK", "STATUS_UPDATE", error);
    return { ok: false, message: errorCode(error) === "VERSION_CONFLICT" ? "Der Stand wurde zwischenzeitlich geändert. Bitte Seite neu laden und erneut versuchen." : "Aufgabenstatus konnte nicht aktualisiert werden." };
  }
}

export async function createSiteAction(_previousState: FormActionState, formData: FormData): Promise<FormActionState> {
  let actor: Actor | null = null;
  try {
    actor = await requireApiActor();
    await createSite(actor, { name: text(formData, "name"), code: text(formData, "code").toUpperCase(), address: text(formData, "address") });
    revalidatePath("/baustellen");
    revalidatePath("/command-center");
    return { ok: true, message: "Baustelle wurde angelegt." };
  } catch (error) {
    await auditFailure(actor, "SITE", "CREATE", error);
    return { ok: false, message: errorCode(error) === "P2002" ? "Das Baustellenkürzel ist bereits vergeben." : "Baustelle konnte nicht angelegt werden. Eingaben und Berechtigungen prüfen." };
  }
}

export async function createChangeOrderAction(_previousState: FormActionState, formData: FormData): Promise<FormActionState> {
  let actor: Actor | null = null;
  try {
    actor = await requireApiActor();
    const baustelleId = text(formData, "baustelleId");
    await authorizeChangeOrderCreationAction(actor, baustelleId);
    await createChangeOrder(actor, {
      baustelleId,
      number: text(formData, "number").toUpperCase(),
      title: text(formData, "title"),
      description: text(formData, "description"),
      cause: text(formData, "cause"),
      occurredOn: text(formData, "occurredOn"),
      laborHours: text(formData, "laborHours"),
      laborRateCents: text(formData, "laborRateCents"),
      machineHours: text(formData, "machineHours"),
      machineRateCents: text(formData, "machineRateCents"),
      materialDescription: text(formData, "materialDescription"),
      materialCostCents: text(formData, "materialCostCents"),
      evidenceStatus: text(formData, "evidenceStatus"),
    });
    revalidatePath("/nachtraege");
    revalidatePath(`/baustellen/${baustelleId}`);
    revalidatePath("/command-center");
    return { ok: true, message: "Nachtragsentwurf wurde intern angelegt. Es wurde nichts versendet oder freigegeben." };
  } catch (error) {
    await auditFailure(actor, "CHANGE_ORDER", "CREATE_DRAFT", error);
    return { ok: false, message: errorCode(error) === "P2002" ? "Die Nachtragsnummer ist bereits vergeben." : "Nachtragsentwurf konnte nicht angelegt werden. Eingaben und Berechtigungen prüfen." };
  }
}

export async function createDailyReportAction(_previousState: FormActionState, formData: FormData): Promise<FormActionState> {
  let actor: Actor | null = null;
  try {
    actor = await requireApiActor();
    const baustelleId = text(formData, "baustelleId");
    await authorizeDailyReportCreationAction(actor, baustelleId);
    await createDailyReport(actor, {
      baustelleId,
      reportDate: text(formData, "reportDate"),
      weather: text(formData, "weather"),
      workPerformed: text(formData, "workPerformed"),
      blockers: text(formData, "blockers"),
      nextSteps: text(formData, "nextSteps"),
    });
    revalidatePath(`/baustellen/${baustelleId}`);
    revalidatePath("/command-center");
    return { ok: true, message: "Tagesbericht wurde als interner Entwurf gespeichert." };
  } catch (error) {
    await auditFailure(actor, "DAILY_REPORT", "CREATE_DRAFT", error);
    return { ok: false, message: "Tagesbericht konnte nicht gespeichert werden. Eingaben und Berechtigungen prüfen." };
  }
}

export async function updateFinanceSettingsAction(_previousState: FormActionState, formData: FormData): Promise<FormActionState> {
  let actor: Actor | null = null;
  try {
    actor = await requireApiActor("finance:configure");
    const toCents = (key: string) => {
      const raw = text(formData, key).trim();
      if (!/^\d{1,8}(?:[.,]\d{1,2})?$/.test(raw)) throw new Error("INVALID_MONEY");
      const [euros, fraction = ""] = raw.replace(",", ".").split(".");
      const cents = BigInt(euros) * BigInt(100) + BigInt(fraction.padEnd(2, "0"));
      if (cents > BigInt(1_000_000_000)) throw new Error("INVALID_MONEY");
      return String(cents);
    };
    await updateFinanceSettings(actor, {
      monthlyRevenueTargetCents: toCents("monthlyRevenueTargetEuros"),
      minimumLiquidityCents: toCents("minimumLiquidityEuros"),
      criticalReceivableDays: text(formData, "criticalReceivableDays"),
      costWarningPercent: text(formData, "costWarningPercent"),
      costCriticalPercent: text(formData, "costCriticalPercent"),
      hoursWarningPercent: text(formData, "hoursWarningPercent"),
      hoursCriticalPercent: text(formData, "hoursCriticalPercent"),
      version: version(formData),
    });
    revalidatePath("/finanzen");
    revalidatePath("/command-center");
    return { ok: true, message: "Warnschwellen wurden gespeichert und auditiert." };
  } catch (error) {
    await auditFailure(actor, "FINANCE", "UPDATE_THRESHOLDS", error);
    return { ok: false, message: errorCode(error) === "VERSION_CONFLICT" ? "Die Einstellungen wurden zwischenzeitlich geändert. Bitte Seite neu laden." : "Finanzschwellen konnten nicht gespeichert werden." };
  }
}

export async function sendChatAction(_previousState: FormActionState, formData: FormData): Promise<FormActionState> {
  let actor: Actor | null = null;
  try {
    actor = await requireApiActor();
    const threadId = nullable(formData, "threadId");
    await authorizeChatThreadAction(actor, threadId);
    const result = await sendChatMessage(actor, { threadId, content: text(formData, "content") });
    revalidatePath("/jarvis");
    return { ok: true, message: "Nachricht wurde sicher gespeichert.", answer: result.assistantMessage?.content, threadId: result.threadId, assistantStatus: result.assistantStatus, actionDraft: result.actionDraft };
  } catch (error) {
    await auditFailure(actor, "CHAT", "MESSAGE_STORED", error);
    return { ok: false, message: "Nachricht konnte nicht gespeichert werden. Bitte erneut versuchen." };
  }
}

export async function prepareProposalAction(_previousState: FormActionState, formData: FormData): Promise<FormActionState> {
  let actor: Actor | null = null;
  try {
    actor = await requireApiActor();
    const threadId = nullable(formData, "threadId");
    await authorizeChatThreadAction(actor, threadId);
    let payload: unknown;
    try {
      payload = JSON.parse(text(formData, "payload"));
    } catch {
      throw new Error("INVALID_JSON");
    }
    if (!payload || Array.isArray(payload) || typeof payload !== "object") throw new Error("INVALID_PAYLOAD");
    await prepareProposal(actor, { title: text(formData, "title"), summary: text(formData, "summary"), category: text(formData, "category"), payload }, threadId);
    revalidatePath("/jarvis");
    revalidatePath("/freigaben");
    revalidatePath("/command-center");
    return { ok: true, message: "Vorschlag wurde zur Freigabe vorbereitet." };
  } catch (error) {
    await auditFailure(actor, "APPROVAL", "PROPOSAL_PREPARED", error);
    const code = errorCode(error);
    if (code === "INVALID_JSON") return { ok: false, message: "Die strukturierten Nutzdaten müssen gültiges JSON sein." };
    if (code === "INVALID_PAYLOAD") return { ok: false, message: "Die strukturierten Nutzdaten müssen ein JSON-Objekt sein." };
    return { ok: false, message: "Vorschlag konnte nicht vorbereitet werden. Eingaben und Berechtigungen prüfen." };
  }
}

export async function decideApprovalAction(_previousState: FormActionState, formData: FormData): Promise<FormActionState> {
  let actor: Actor | null = null;
  try {
    actor = await requireApiActor();
    const requestId = text(formData, "requestId");
    await authorizeApprovalDecisionAction(actor, requestId);
    await decideApprovalRequest(actor, { requestId, decision: text(formData, "decision"), note: text(formData, "note"), version: version(formData) });
    revalidatePath("/freigaben");
    revalidatePath("/command-center");
    revalidatePath("/audit");
    return { ok: true, message: "Menschliche Entscheidung wurde dokumentiert." };
  } catch (error) {
    await auditFailure(actor, "APPROVAL", "HUMAN_DECISION", error);
    return { ok: false, message: errorCode(error) === "VERSION_CONFLICT" || errorCode(error) === "INVALID_TRANSITION" ? "Die Freigabe wurde bereits bearbeitet. Bitte Seite neu laden." : "Entscheidung konnte nicht gespeichert werden." };
  }
}


export async function confirmAssistantIntentAction(_previousState: FormActionState, formData: FormData): Promise<FormActionState> {
  let actor: Actor | null = null;
  try {
    actor = await requireApiActor("chat:use");
    await authorizeChatThreadAction(actor, nullable(formData, "threadId"));
    const intent = validateConfirmation(actor, { type: text(formData, "type"), title: text(formData, "title"), detail: text(formData, "detail") },
      { organizationId: text(formData, "organizationId"), userId: text(formData, "userId") }, text(formData, "confirmed") === "yes");
    const data = new FormData();
    if (intent.type === "CREATE_TASK") {
      data.set("title", intent.title); data.set("description", intent.detail); data.set("priority", "MEDIUM");
      return await createTaskAction({ ok: false, message: "" }, data);
    }
    const { captureOperationsEntry } = await import("@/lib/services/operations-persistence");
    await captureOperationsEntry(actor, { confirmed: true, category: "NOTIZ", title: intent.title, content: intent.detail || intent.title,
      source: "CHAT", sourceReference: [text(formData, "threadId"), text(formData, "sourceReference")].filter(Boolean).join(":") });
    revalidatePath("/operations"); revalidatePath("/jarvis");
    return { ok: true, message: "Notiz dauerhaft in der Operations-Inbox gespeichert. Business Memory bei Bedarf mit Zweck und Prüffrist aktivieren." };
  } catch (error) {
    await auditFailure(actor, "CHAT", "CONFIRM_INTENT", error);
    return { ok: false, message: "Entwurf nicht gespeichert. Bestätigung, Berechtigung und Inhalt prüfen. REVIEW und CRITICAL bleiben Vorbereitung." };
  }
}
